// Builds the POST /api/ai-interviews/create payload that turns a library AI
// interview into a screening's own copy, linked back via libraryTemplateId so
// later library edits reach it. Type-specific fields come from ./standard.js or
// ./rubric.js; everything here is shared by both types.
import * as standard from './standard';
import * as rubric from './rubric';
import { currentVoiceId } from '../replacedVoices';

// Keep in sync with LANGUAGE_OPTIONS / server utils/languageCodes.js.
const VALID_LANGUAGE_CODES = ['en', 'hi', 'hi-en', 'en-hi', 'kn', 'ta', 'bn', 'te', 'mr', 'gu', 'ml', 'pa', 'tl', 'fr'];
const DEFAULT_HINDI_VOICE_ID = '0f14d8cb-f039-41fe-a813-a9b4bee7eed8'; // Nisha

export function getInterviewType(template) {
  return rubric.isRubricTemplate(template) ? rubric : standard;
}

export function normalizeLanguageCode(raw) {
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (v === 'hi' || v === 'hindi') return 'hi';
  if (v === 'hi-en' || v === 'hinglish') return 'hi-en';
  return VALID_LANGUAGE_CODES.includes(v) ? v : 'en';
}

/**
 * @returns {object|null} the create payload, or null when the template has no questions
 */
export function buildInterviewFromLibraryTemplate(template, { templateId, jobId, fallbackTitle }) {
  const type = getInterviewType(template);
  if (!type.hasQuestions(template)) return null;

  const languageCode = normalizeLanguageCode(template?.config?.language || template?.typeConfig?.language);
  // Non-English languages use a regional voice pool; the Hindi voice is the
  // fallback when the template has no voice saved.
  const usesRegionalVoicePool = languageCode !== 'en';
  const { questions, rubrics, maxQuestions } = type.buildQuestionFields(template);

  return {
    title: template.title || fallbackTitle || 'AI Interview',
    role: template.role || 'IT',
    context: template?.config?.context || template.description || '',
    type: template?.config?.type || 'voice',
    jobPostingId: jobId,
    libraryTemplateId: templateId,
    deliveryMode: 'livekit',
    aiIntro: template.aiIntro || '',
    aiOutro: template.aiOutro || '',
    voiceId: template.voiceId ? currentVoiceId(template.voiceId) : usesRegionalVoicePool ? DEFAULT_HINDI_VOICE_ID : undefined,
    questions,
    rubrics,
    config: {
      duration: 30,
      maxQuestions,
      topics: [],
      difficulty: (template.difficulty || 'Medium').toLowerCase(),
      language: languageCode,
      personality: template?.config?.personality || 'professional',
    },
    advancedType: template.advancedType || null,
    typeConfig: template.advancedType ? { language: languageCode } : null,
  };
}
